import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { LogoComponent } from '../../shared/components/logo/logo.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { SessionService } from '../../shared/services/session/session.service';
import { StoreService } from '../../shared/services/store/store.service';
import { StyleService } from '../../shared/services/style/style.service';
import { MessageService } from '../../shared/services/message/message.service';
import { flowStepFromUrl, FlowStateService } from '../../shared/services/flow-state/flow-state.service';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [RouterModule, SpinnerComponent, LogoComponent],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly sessionService = inject(SessionService);
  private readonly store = inject(StoreService);
  private readonly styleService = inject(StyleService);
  private readonly messageService = inject(MessageService);
  private readonly flowStateService = inject(FlowStateService);

  // Styles
  readonly brandColor = computed(() => this.styleService.styles().brand.color);

  // Loading flags
  private sessionLoading = signal<boolean>(true);
  private flagsLoading = signal<boolean>(true);
  readonly isLoading = computed(() => this.sessionLoading() || this.flagsLoading());

  // Routes where the layout 'Go back' button should be hidden
  readonly hideGoBackButton = this.flowStateService.showGoBack;

  constructor() {
    // If the session service fails to load the session info, navigate to a warning step
    effect(() => {
      const err = this.sessionService.hasError();

      if (err) {
        // NOTE: might want to move to error screen instead of warning
        this.sessionLoading.set(false);
        this.flagsLoading.set(false);
        this.router.navigate(['warning'], { relativeTo: this.route });
      } else {
        untracked(() => this.sessionLoading.set(false));
      }
    });

    // Navigate on initial session flags
    effect(() => {
      const flags = this.store.flags();

      // Only continue if flags are defined
      if (!flags || flags.onlyShowApplication === undefined) return;

      if (flags.onlyShowApplication) {
        this.router.navigate(['application'], { relativeTo: this.route });
      } else {
        untracked(() => this.flagsLoading.set(false));
      }
    });
  }

  /**
   * On go back, use the flow state service to determine action. Either close the modal or move to a specified step
   */
  onGoBack(): void {
    const currentStep = flowStepFromUrl(this.router.url);
    if (!currentStep) {
      this.closeModal();
      return;
    }

    const action = this.flowStateService.getGoBackAction(currentStep);
    if (action.type === 'navigate') {
      this.router.navigate([action.step], { relativeTo: this.route });
      return;
    }

    this.closeModal();
  }

  private closeModal(): void {
    const token = this.store.token();
    if (token && token.trim() !== '') {
      this.messageService.closeModalAndSession();
    } else {
      this.messageService.onlyCloseModal();
    }
  }
}
